import { useHandler } from "@redotech/react-util/hook";
import { AutocheckPricingTestActiveSplit } from "@redotech/redo-model/conversion/conversion-autocheck";
import { PRICING_TEST_TREATMENT_KEY } from "@redotech/redo-model/shopify/cart";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { PRICING_TEST_TREATMENT_ID_LOCAL_STORAGE_KEY } from "@redotech/redo-web/utils/shared-conf";
import { useEffect, useMemo } from "react";
import { StorefrontClient } from "../../../client/storefront";

const PRICING_TEST_TREATMENT_DURATION_HOURS = 1;

type PricingTestState =
  | {
      active: true;
      treatmentId: string;
      variantPrices: Map<string, number>;
      getVariantPrice: (variantId: string) => number | undefined;
    }
  | { active: false };

export function usePricingTest({
  autocheckPricingTest,
  storefrontClient,
}: {
  autocheckPricingTest: AutocheckPricingTestActiveSplit | null;
  storefrontClient: StorefrontClient;
}): PricingTestState {
  const selectedTreatment = useMemo(() => {
    if (!autocheckPricingTest) {
      return undefined;
    }

    // Check localStorage first for sticky treatment assignment
    const storedTreatmentId = getLocalStorageWithExpiry(
      PRICING_TEST_TREATMENT_ID_LOCAL_STORAGE_KEY,
    );

    if (storedTreatmentId) {
      // Find the treatment from autocheck that matches stored ID
      const storedTreatment = autocheckPricingTest.treatments.find(
        (treatment) => treatment._id === storedTreatmentId,
      );
      if (storedTreatment) {
        return storedTreatment;
      }
    }

    // Use server-selected treatment for new visitors
    return autocheckPricingTest.treatments.find(
      (treatment) => treatment._id === autocheckPricingTest.selectedTreatmentId,
    );
  }, [autocheckPricingTest]);

  const variantPrices = useMemo(() => {
    if (!selectedTreatment) {
      return new Map<string, number>();
    }

    const priceMap = new Map<string, number>();
    for (const vp of selectedTreatment.pricingConfig.variantPrices) {
      priceMap.set(vp.variantId, vp.price);
    }
    return priceMap;
  }, [selectedTreatment]);

  useEffect(() => {
    if (selectedTreatment) {
      // Store treatment ID for sticky assignment across page loads
      setLocalStorageWithExpiry(
        PRICING_TEST_TREATMENT_ID_LOCAL_STORAGE_KEY,
        selectedTreatment._id,
        PRICING_TEST_TREATMENT_DURATION_HOURS,
      );
    }
  }, [selectedTreatment]);

  useEffect(() => {
    if (!selectedTreatment) {
      return;
    }

    const setCartAttribute = async () => {
      try {
        // Only pass treatment ID - pricing config is stored in shop metafield
        // for security (prevents client-side price manipulation)
        await storefrontClient.updateCartAttribute({
          attributes: [
            { key: PRICING_TEST_TREATMENT_KEY, value: selectedTreatment._id },
          ],
        });
      } catch (err) {
        console.error("Failed to set pricing test cart attribute", err);
      }
    };

    void setCartAttribute();
  }, [selectedTreatment, storefrontClient]);

  const getVariantPrice = useHandler((variantId: string) => {
    return variantPrices.get(variantId);
  });

  const pricingTestState: PricingTestState = useMemo(() => {
    if (!selectedTreatment) {
      return { active: false };
    }

    return {
      active: true,
      treatmentId: selectedTreatment._id,
      variantPrices,
      getVariantPrice,
    };
  }, [selectedTreatment, variantPrices, getVariantPrice]);

  return pricingTestState;
}
